import { getContentDependencyErrorMessage } from "@/components/content-dependency/DependenciesList";
import { useIsApiReady } from "@/hooks/use-api-ready";
import {
  grantControllerList,
  grantControllerRevoke,
  grantControllerSetPublished,
  grantControllerUpsert,
  type GetGrantsResponseDto,
} from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

type GrantDto = GetGrantsResponseDto["data"][number];

export type ShareAccess = Exclude<GrantDto["access"], "owner">;

export type GrantParams =
  | { kitUuid: string | null }
  | { skillUuid: string | null }
  | { folderUuid: string | null }
  | { templateUuid: string | null }
  | { scriptUuid: string | null }
  | { expertUuid: string | null };

export const useGrantsData = (resourceUuids: GrantParams) => {
  const queryClient = useQueryClient();
  const isReady = useIsApiReady();

  const resourceKey = Object.keys(resourceUuids).find((key) =>
    Boolean(resourceUuids[key as keyof typeof resourceUuids]),
  );
  const uuid = resourceUuids[resourceKey as keyof typeof resourceUuids]!;

  const { data: grants, isPending } = useQuery({
    enabled: isReady && resourceKey !== undefined,
    queryKey: queryKeys.grants(resourceKey!, uuid),
    queryFn: () =>
      grantControllerList({
        query: { [resourceKey!]: uuid },
      }).then((res) => res.data?.data ?? []),
  });

  const invalidate = () => {
    if (!resourceKey) return;

    queryClient.invalidateQueries({
      queryKey: queryKeys.grants(resourceKey, uuid),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.group(),
    });
  };

  const { mutateAsync: upsert, isPending: isUpserting } = useMutation({
    mutationFn: async (vars: { groupUuid: string; access: ShareAccess }) => {
      if (!resourceKey) return;

      const res = await grantControllerUpsert({
        body: {
          [resourceKey]: uuid,
          groupUuid: vars.groupUuid,
          access: vars.access,
        },
      });

      if (res.response.status === 409) {
        toast.error(
          getContentDependencyErrorMessage(res.error) ??
            "Failed to grant access because a dependency is unavailable.",
        );
      }
    },
    onSettled: invalidate,
  });

  const { mutateAsync: revoke, isPending: isRevoking } = useMutation({
    mutationFn: async (vars: { groupUuid: string }) => {
      if (!resourceKey) return;
      return grantControllerRevoke({
        body: {
          [resourceKey]: uuid,
          groupUuid: vars.groupUuid,
        },
      });
    },
    onSettled: invalidate,
  });

  const { mutateAsync: setPublished, isPending: isPublishing } = useMutation({
    mutationFn: async (isPublished: boolean) => {
      if (!resourceKey) return;
      return grantControllerSetPublished({
        body: {
          [resourceKey]: uuid,
          isPublished,
        },
      });
    },
    onSettled: invalidate,
  });

  return {
    grants: grants ?? [],
    isLoading: isPending || isUpserting || isRevoking || isPublishing,
    upsert,
    revoke,
    setPublished,
  };
};
